import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import 'moment/locale/fr';
import Box from '../../shared/atoms/Box';
import Text from '../../shared/atoms/Text';
import Heading from '../../shared/atoms/Heading';
import Button from '../../shared/atoms/Button';
import Row from '../../shared/atoms/Row';
import Back16 from '../../icons/Back16';
import CalendarProposalColor32 from '../../icons/CalendarProposalColor32';
import AgendaCheckbox from '../../dateTimeModule/AgendaCheckbox';
import { getYieldBonusAmount } from '../../../utils/slots';

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
`;

const TableHeader = styled.th`
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  border: 1px solid ${(p) => p.theme.colors.gray.light};
  background-color: ${(p) => p.theme.colors.gray.lightest};
  text-transform: capitalize;
`;

const TableCell = styled.td`
  padding: 8px;
  border: 1px solid ${(p) => p.theme.colors.gray.light};
  vertical-align: top;
`;

const SlotsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobileDaysWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MobileDayCard = styled(Box)`
  border: 1px solid ${(p) => p.theme.colors.gray.light};
  border-radius: ${(p) => p.theme.radii.sm};
  padding: 12px;
`;

const MobileDayHeader = styled(Text)`
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: capitalize;
`;

const EmptyState = styled(Box)`
  text-align: center;
  padding: 24px;
  background-color: ${(p) => p.theme.colors.gray.lightest};
  border-radius: ${(p) => p.theme.radii.sm};
`;

const CounterProposalsList = ({
  courseId,
  proposals = [],
  onAccepted,
  onRefresh,
  onSelectProposal,
  zoneId,
}) => {
  // Les majorations ne s'affichent que pour la zone Paris (zone_id === 1)
  const showYieldBonus = zoneId === 1;
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  // Set moment locale based on current language
  useEffect(() => {
    moment.locale(i18n.language === 'fr' ? 'fr' : 'en');
  }, [i18n.language]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const handleChange = (e) => setIsMobile(e.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Filter only available proposals
  const availableProposals = useMemo(() => {
    return proposals.filter((p) => p.is_available);
  }, [proposals]);

  // Group proposals by day
  const groupedProposals = useMemo(() => {
    const groups = {};

    availableProposals.forEach((proposal) => {
      const date = moment(proposal.proposed_slot).format('YYYY-MM-DD');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(proposal);
    });

    // Sort slots within each day
    Object.keys(groups).forEach((date) => {
      groups[date].sort(
        (a, b) =>
          moment(a.proposed_slot).valueOf() - moment(b.proposed_slot).valueOf(),
      );
    });

    return groups;
  }, [availableProposals]);

  // Get sorted dates
  const sortedDates = useMemo(() => {
    return Object.keys(groupedProposals).sort(
      (a, b) => moment(a).valueOf() - moment(b).valueOf(),
    );
  }, [groupedProposals]);

  const formatDayHeader = (dateStr) => {
    const date = moment(dateStr);
    // Format plus compact, ex: "ven. 5 déc."
    return date.format('ddd D MMM');
  };

  const formatSlotTime = (slotDateTime) => {
    return moment(slotDateTime).format('HH:mm');
  };

  const handleSlotClick = (proposal) => {
    if (onSelectProposal) {
      onSelectProposal(proposal);
    }
  };

  if (availableProposals.length === 0) {
    return (
      <Box
        borderColor="gray.light"
        borderWidth="1px"
        borderRadius="sm"
        borderStyle="solid"
        p={4}
        mt={5}>
        <Row md gap={16}>
          <CalendarProposalColor32 />
          <Box>
            <Heading level={6} mb={2}>
              {t('counterProposals.title')}
            </Heading>
            <EmptyState>
              <Text color="gray.dark">{t('counterProposals.empty')}</Text>
            </EmptyState>
            <Button
              variant="neutral"
              size="compact"
              before={<Back16 />}
              mt={3}
              onClick={onRefresh}>
              {t('counterProposals.refresh')}
            </Button>
          </Box>
        </Row>
      </Box>
    );
  }

  return (
    <Box
      borderColor="gray.light"
      borderWidth="1px"
      borderRadius="sm"
      borderStyle="solid"
      p={4}
      mt={5}>
      <Row md gap={16} mb={4}>
        <CalendarProposalColor32 />
        <Box style={{ flex: 1 }}>
          <Heading level={6} mb={1}>
            {t('counterProposals.title')}
          </Heading>
          <Text color="gray.dark" fontSize={1}>
            {t('counterProposals.subtitle', {
              count: availableProposals.length,
            })}
          </Text>
        </Box>
      </Row>

      {isMobile ? (
        <MobileDaysWrapper>
          {sortedDates.map((date) => (
            <MobileDayCard key={date}>
              <MobileDayHeader as="div">
                {formatDayHeader(date)}
              </MobileDayHeader>
              <SlotsList>
                {groupedProposals[date].map((proposal) => {
                  const yieldAmount = showYieldBonus
                    ? getYieldBonusAmount(proposal.proposed_slot)
                    : 0;
                  return (
                    <AgendaCheckbox
                      key={proposal.id}
                      text={formatSlotTime(proposal.proposed_slot)}
                      yieldAmount={yieldAmount}
                      onClick={() => handleSlotClick(proposal)}
                    />
                  );
                })}
              </SlotsList>
            </MobileDayCard>
          ))}
        </MobileDaysWrapper>
      ) : (
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                {sortedDates.map((date) => (
                  <TableHeader key={date}>{formatDayHeader(date)}</TableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {sortedDates.map((date) => (
                  <TableCell key={date}>
                    <SlotsList>
                      {groupedProposals[date].map((proposal) => {
                        const yieldAmount = showYieldBonus
                          ? getYieldBonusAmount(proposal.proposed_slot)
                          : 0;
                        return (
                          <AgendaCheckbox
                            key={proposal.id}
                            text={formatSlotTime(proposal.proposed_slot)}
                            yieldAmount={yieldAmount}
                            onClick={() => handleSlotClick(proposal)}
                          />
                        );
                      })}
                    </SlotsList>
                  </TableCell>
                ))}
              </tr>
            </tbody>
          </StyledTable>
        </TableWrapper>
      )}

      {/* Info sur les majorations */}
      {showYieldBonus &&
        availableProposals.some(
          (p) => getYieldBonusAmount(p.proposed_slot) > 0,
        ) && (
          <Box
            mt={3}
            p={2}
            borderRadius="sm"
            bg="gray.lightest"
            borderColor="gray.light"
            borderWidth="1px"
            borderStyle="solid">
            <Text fontSize="14px" color="gray.dark">
              {t('counterProposals.bonusInfo')}
            </Text>
          </Box>
        )}

      <Box mt={4} mb={3}>
        <Button
          variant="neutral"
          size="compact"
          before={<Back16 />}
          onClick={onRefresh}>
          {t('counterProposals.refresh')}
        </Button>
      </Box>

      <Box mt={2} p={3} borderRadius="sm" bg="gray.lightest">
        <Text mb={2} fontWeight={2}>
          💡 Vous pouvez :
        </Text>
        <Box as="ul" pl={3} m={0} style={{ listStyleType: 'disc' }}>
          <li>
            <Text as="span">
              <strong>Accepter</strong> l’un des créneaux proposés (validation
              instantanée)
            </Text>
          </li>
          <li>
            <Text as="span">
              <strong>Attendre</strong> la réponse d’autres réparateurs
            </Text>
          </li>
          <li>
            <Text as="span">
              <strong>Ajouter des disponibilités</strong> supplémentaires
            </Text>
          </li>
        </Box>
      </Box>
    </Box>
  );
};

export default CounterProposalsList;
